import { useContext, useState } from 'react';
import { fetchWithTokenRefresh } from '../../../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../../../api/endpoints';
import { useDialog } from '../../../../../../../common/dialog/useDialog';
import { ToastNotificationContext } from '../../../../../../../common/toast-notification';
import { DialogError } from '../../../../../../../common/business-error';
import { handleResponseError } from '../../../../../../../../api/handleResponseError';
import { ToastProps } from '../../../../../../../common/toast-notification/interfaces';
import { userDeleteInvitationBusinessError } from '../businessErrors';

type ApiResponse = {
  message: string;
  status: ApiResponseStatus;
};

enum ApiResponseStatus {
  OK = 'OK',
  FAILED = 'FAILED'
}

interface Props {
  handleSuccess: () => void;
}

export const useDeleteUserInvitation = ({ handleSuccess }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [deleteLoading, setLoading] = useState(false);
  const [deleteError, setError] = useState<DialogError>(null);

  const handleDeleteUserInvitation = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithTokenRefresh(`${endpoints.accountsManagementInactive}/${id}`, {
        method: 'delete'
      });
      const jsonResponse = await response.json();

      if (response.status !== 200) {
        await handleResponseError(response);
      }

      const parsed: ApiResponse = jsonResponse;
      switch (parsed.status) {
        case ApiResponseStatus.OK: {
          openToast({
            message: 'Invitation was successfully deleted',
            severity: 'success'
          });

          setLoading(false);
          handleCloseDialog();
          handleSuccess();
          break;
        }
        case ApiResponseStatus.FAILED: {
          const toastResponse: ToastProps = {
            message: 'Failed to remove invitation',
            severity: 'error'
          };
          openToast(toastResponse);
          setError({ message: toastResponse.message, severity: 'error' });
          break;
        }
        default: {
          openToast({
            message: 'unknown error',
            severity: 'error'
          });
        }
      }

      setLoading(false);
    } catch (e) {
      const { message, severity } = userDeleteInvitationBusinessError();

      setError({ message, severity });
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    deleteUserInvitationModalOpen: isDialogOpen,
    handleOpenDeleteUserInvitationModal: handleOpenDialog,
    handleCloseDeleteUserInvitationModal: closeDialog,
    handleDeleteUserInvitation,
    deleteLoading,
    deleteError,
    setError
  };
};
